<template>
  <div class="article__comment-box">
    <h5 class="inline-block text-md px-3.5 py-2.5 bg-[#E2E5ED] font-bold rounded-t-md">{{ $t('article-comment.sub-header') }}</h5>
    <div ref="commentAreaRef" class="article__comment-box-inner flex flex-col bg-[#E2E5ED] p-2.5 rounded rounded-tl-none">
      <div class="flex">
        <OAvatar :src="userAvatar" class="flex flex-shrink-0 w-11 h-11 rounded-full text-26 border-none mr-2.5"></OAvatar>
        <textarea
          v-model="text"
          class="
            textarea--comment
            rounded-sm
            w-full
            p-2.5
            text-md
            placeholder-alpha-40
            focus:ring
            ring-1 ring-black-5
            focus:ring-blue-300 focus:outline-none
          "
          :class="`${isFullSize ? 'h-[80px] sm:h-[100px]' : 'h-[50px]'}`"
          :placeholder="totalComments > 0 ? `${$t('article-comment.share-comment')} 🙂` : `${$t('article-comment.first-comment')} 🙂`"
          @focus="focus"
        ></textarea>
      </div>
      <OButton v-if="isFullSize" class="ml-auto text-md font-medium m-2.5 sm:mr-0 sm:mb-0" size="xs" color-scheme="green" @click="submit">
        {{ $t('article-comment.send') }}
      </OButton>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, ref, useContext, watch, inject } from '@nuxtjs/composition-api';
import { sendReactionCommentLayer } from '@/modules/tracking/scripts/dataLayer';
import { debounce } from 'vue-composable';
import { OButton, OAvatar } from '~/modules/common/components/ui';
import { useAuth } from '~/modules/auth/state/useAuth';
import { useDevice } from '~/composables/useDevice';
import { useOutsideClick } from '~/composables/useOutsideClick';
import { useDialog, useModal } from '~/composables/useModal';
import { getImageUrl } from '~/modules/common/utils/imageUtils';

export default defineComponent({
  props: {
    /**
     * article id
     */
    articleId: {
      type: [String, Number],
      default: null,
    },

    /**
     * yanitla(reply) ve düzenle(update) için kullanılan comment objesi
     *
     */
    comment: {
      type: [Object, null],
      default: null,
    },

    /**
     * Yorum kutusunun modu
     *
     * oluşturma yanıtla ve güncelleme için kullanılabilir
     */
    mode: {
      type: String,
      default: 'create',
      validator: (value) => {
        return ['create', 'reply', 'update'].includes(value);
      },
    },
  },
  components: { OAvatar, OButton },
  setup(props, { emit }) {
    const { $service, $analyticsEvent, i18n } = useContext();
    const { user, isLoggedIn } = useAuth();
    const { isMobile } = useDevice();
    const { openLoginModal, openModal } = useModal();
    const { openErrorDialog } = useDialog();

    // Changing placeholder depending on total comments
    const { comments } = inject('comments');
    const article = inject('article');
    const adConfig = inject('adConfig');
    const totalComments = computed(() => comments.value.length);
    const text = ref('');

    const commentAreaRef = ref();

    const shouldBeFullSize = computed(() => {
      // if (props.mode === 'reply') return true;
      // if (isMobile.value) return true;
      return false;
    });

    const isFullSize = ref(false);
    watch(
      [isMobile],
      () => {
        if (shouldBeFullSize.value) {
          isFullSize.value = true;
        } else {
          isFullSize.value = false;
        }
      },
      { immediate: true }
    );

    useOutsideClick(commentAreaRef, (e) => {
      // yorum yazma kutusunun dışındaki herhangi bir yere/butona(yanıtla,beğen gibi) tıklandığında sayfanin hemen kaymamasi için settimeout ile biraz bekletiyoruz
      setTimeout(() => {
        if (isFullSize.value && !shouldBeFullSize.value) {
          isFullSize.value = false;
        }
      }, 300);
    });
    const userAvatar = computed(() => getImageUrl(user.value?.avatar, { width: 100 }));

    const addComment = async () => {
      const commentData = {
        id: props.articleId,
        text: text.value,
      };
      const { data } = await $service.article.sendComment(commentData);
      return data;
    };

    const addReply = async () => {
      const replyData = {
        text: text.value,
        replyToId: props.comment?._id, // replyTo yok ise ilk yoruma yanit veriliyordur
        parentId: props.comment?.parent?._id || props.comment?._id, // parent yok ise ilk yoruma yanit veriliyordur
      };
      const { data } = await $service.article.sendReply(replyData);
      return data;
    };

    const focus = () => {
      if (!isLoggedIn.value) {
        openLoginModal();
      } else {
        isFullSize.value = true;
      }
    };

    const submit = debounce(async () => {
      if (!isLoggedIn.value) {
        await openLoginModal();
        return;
      }
      if (isLoggedIn.value && !user.value?.emailVerified) {
        openModal(import('~/modules/common/components/modals/CommentEmailVerify' /* webpackChunkName: "CommentEmailVerify" */));
        return;
      }
      if (text.value.length < 5) {
        openErrorDialog({ description: i18n.t('article-comment.dialog-text-2') });
        return;
      }
      if (text.value.length > 1000) {
        openErrorDialog({ description: i18n.t('article-comment.dialog-text-3') });
        return;
      }
      try {
        if (props.mode === 'reply') {
          const result = await addReply();
          emit('submit', result);
          $analyticsEvent('User', 'User_Engagement', 'action_reply');
          sendReactionCommentLayer('comment', { article, adConfig });
          text.value = '';
        } else {
          const result = await addComment();
          emit('submit', result);
          $analyticsEvent('User', 'User_Engagement', 'action_comment');
          sendReactionCommentLayer('comment', { article, adConfig });
          text.value = '';
        }
      } catch (error) {
        openErrorDialog({ error });
      }
    }, 300);

    return { text, commentAreaRef, isFullSize, userAvatar, submit, focus, totalComments };
  },
});
</script>

<style lang="postcss">
.textarea--comment {
  transition: height 0.2s ease-in-out;
}
</style>
